import React, { useState, useEffect, useCallback } from 'react';
import { Wallet, Percent, Save, TrendingUp, TrendingDown, RefreshCw, Settings2, Scale, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { useLanguage } from '../../context/LanguageContext';
import { ManualTxDialog, ManualTxList, ManualTxButton } from './ManualTransactions';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => Number(n || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 });

const PROVIDERS = [
    { id: 'wave', label: 'Wave', accent: 'text-blue-400', border: 'border-blue-500/30' },
    { id: 'orange_money', label: 'Orange Money', accent: 'text-orange-400', border: 'border-orange-500/30' },
];

const BaselineDialog = ({ open, onClose, provider, baseline, auth, onSaved }) => {
    const { t } = useLanguage();
    const [theorique, setTheorique] = useState(baseline?.initial_theorique || 0);
    const [reel, setReel] = useState(baseline?.initial_reel || 0);
    const [resetHistory, setResetHistory] = useState(!!baseline?.ignore_history_before);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setTheorique(baseline?.initial_theorique || 0);
        setReel(baseline?.initial_reel || 0);
        setResetHistory(!!baseline?.ignore_history_before);
    }, [baseline, open]);

    const save = async () => {
        setSaving(true);
        try {
            await axios.put(`${API}/payments/apps/baselines`, {
                provider: provider.id,
                initial_theorique: parseFloat(theorique) || 0,
                initial_reel: parseFloat(reel) || 0,
                reset_history: resetHistory,
            }, auth);
            toast.success(t(`Solde ${provider.label} initialisé`, `${provider.label} balance initialized`));
            onSaved();
            onClose();
        } catch (e) { toast.error(t('Erreur de sauvegarde', 'Save failed')); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="bg-slate-900 border-slate-700 text-white">
                <DialogHeader>
                    <DialogTitle>{t(`Initialiser le solde ${provider.label}`, `Initialize ${provider.label} balance`)}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label className="text-slate-300">{t('Ajustement initial — solde théorique (XOF)', 'Initial adjustment — theoretical balance (XOF)')}</Label>
                        <Input type="number" step="0.01" value={theorique} onChange={(e) => setTheorique(e.target.value)}
                            className="bg-slate-800 border-slate-600 text-white mt-1" data-testid={`baseline-theorique-${provider.id}`} />
                    </div>
                    <div>
                        <Label className="text-slate-300">{t('Ajustement initial — solde réel (XOF)', 'Initial adjustment — real balance (XOF)')}</Label>
                        <Input type="number" step="0.01" value={reel} onChange={(e) => setReel(e.target.value)}
                            className="bg-slate-800 border-slate-600 text-white mt-1" data-testid={`baseline-reel-${provider.id}`} />
                    </div>
                    <div className="flex items-center gap-2">
                        <Checkbox checked={resetHistory} onCheckedChange={setResetHistory} id={`reset-${provider.id}`} data-testid={`baseline-reset-${provider.id}`} />
                        <label htmlFor={`reset-${provider.id}`} className="text-sm text-slate-300 cursor-pointer">
                            {t("Ignorer l'historique passé (repartir de ce solde, seules les nouvelles transactions comptent)",
                               'Ignore past history (start from this balance, only new transactions count)')}
                        </label>
                    </div>
                    {baseline?.ignore_history_before && (
                        <p className="text-xs text-slate-500">{t('Historique ignoré avant le', 'History ignored before')} {new Date(baseline.ignore_history_before).toLocaleString('fr-FR')}</p>
                    )}
                    <Button onClick={save} disabled={saving} className="w-full bg-blue-600 hover:bg-blue-700" data-testid={`baseline-save-${provider.id}`}>
                        <Save size={14} className="mr-2" /> {t('Enregistrer', 'Save')}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

const ReconCard = ({ recon, provider }) => {
    const { t } = useLanguage();
    if (!recon) return null;
    if (recon.status === 'error') {
        return (
            <div className="mt-3 flex items-start gap-2 text-xs bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid={`recon-error-${provider.id}`}>
                <XCircle size={14} className="text-red-400 mt-0.5 shrink-0" />
                <div>
                    <span className="text-red-300 font-semibold">{t('Rapprochement impossible', 'Reconciliation failed')} : </span>
                    <span className="text-red-300/80">{recon.error}</span>
                </div>
            </div>
        );
    }
    const ok = recon.status === 'ok';
    return (
        <div className={`mt-3 flex items-center gap-3 flex-wrap text-xs rounded-lg px-3 py-2 border ${ok ? 'bg-green-500/10 border-green-500/30' : 'bg-amber-500/10 border-amber-500/40'}`}
            data-testid={`recon-result-${provider.id}`}>
            {ok ? <CheckCircle2 size={14} className="text-green-400" /> : <AlertTriangle size={14} className="text-amber-400" />}
            <span className="text-slate-300">{t('Solde compte (API)', 'Account balance (API)')} : <b className="text-white">{fmt(recon.live_balance)} {recon.currency}</b></span>
            <span className="text-slate-300">{t('Théorique', 'Theoretical')} : <b className="text-white">{fmt(recon.solde_theorique)} XOF</b></span>
            <span className={ok ? 'text-green-400 font-bold' : 'text-amber-400 font-bold'} data-testid={`recon-ecart-${provider.id}`}>
                {t('Écart', 'Gap')} : {recon.ecart > 0 ? '+' : ''}{fmt(recon.ecart)} XOF {ok ? '✓' : ''}
            </span>
            {!ok && <span className="text-amber-300/80">{t('→ enregistrez un ajustement libre pour corriger', '→ record a free adjustment to fix')}</span>}
        </div>
    );
};

const ProviderBalance = ({ provider, data, auth, onRefresh, recon }) => {
    const { t } = useLanguage();
    const [edits, setEdits] = useState({});
    const [saving, setSaving] = useState(null);
    const [baselineOpen, setBaselineOpen] = useState(false);
    const [manualOpen, setManualOpen] = useState(false);

    const savePct = async (appName) => {
        const pct = parseFloat(edits[appName]);
        if (isNaN(pct) || pct < 0 || pct > 100) { toast.error(t('Pourcentage invalide (0-100)', 'Invalid percentage (0-100)')); return; }
        setSaving(appName);
        try {
            await axios.put(`${API}/payments/apps/config`, { app_name: appName, commission_pct: pct }, auth);
            toast.success(`${appName} : ${pct}%`);
            setEdits((e) => { const n = { ...e }; delete n[appName]; return n; });
            onRefresh();
        } catch (e) { toast.error(t('Erreur de sauvegarde', 'Save failed')); }
        finally { setSaving(null); }
    };

    const { apps = [], totals = {}, baseline = {}, manual_adjustments: manualAdj = {}, manual_transactions: manualTxs = [] } = data || {};
    const hasBaseline = (baseline.initial_theorique || 0) !== 0 || (baseline.initial_reel || 0) !== 0 || baseline.ignore_history_before;
    const hasManualAdj = (manualAdj.delta_theorique || 0) !== 0 || (manualAdj.delta_reel || 0) !== 0;

    return (
        <div className={`glass-card p-5 mb-6 border ${provider.border}`} data-testid={`provider-balance-${provider.id}`}>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h3 className={`text-base font-bold ${provider.accent} flex items-center gap-2`}>
                    <Wallet size={17} /> {provider.label}
                </h3>
                <div className="flex gap-2">
                    <ManualTxButton onClick={() => setManualOpen(true)} provider={provider} />
                    <Button size="sm" variant="outline" onClick={() => setBaselineOpen(true)}
                        className="border-slate-600 text-slate-300" data-testid={`init-balance-btn-${provider.id}`}>
                        <Settings2 size={13} className="mr-1" /> {t('Initialiser le solde', 'Initialize balance')}
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="bg-slate-800/60 rounded-lg p-3">
                    <p className="text-xs text-slate-400 flex items-center gap-1"><TrendingUp size={12} className="text-green-400" /> {t('Crédits', 'Credits')}</p>
                    <p className="text-lg font-bold text-green-400">{fmt(totals.credits)} XOF</p>
                </div>
                <div className="bg-slate-800/60 rounded-lg p-3">
                    <p className="text-xs text-slate-400 flex items-center gap-1"><TrendingDown size={12} className="text-red-400" /> {t('Débits', 'Debits')}</p>
                    <p className="text-lg font-bold text-red-400">{fmt(totals.debits)} XOF</p>
                </div>
                <div className="bg-slate-800/60 rounded-lg p-3" data-testid={`theorique-total-${provider.id}`}>
                    <p className="text-xs text-slate-400">{t('Solde théorique', 'Theoretical balance')}</p>
                    <p className="text-lg font-bold text-white">{fmt(totals.solde_theorique)} XOF</p>
                </div>
                <div className="bg-slate-800/60 rounded-lg p-3" data-testid={`reel-total-${provider.id}`}>
                    <p className="text-xs text-slate-400">{t('Solde réel', 'Real balance')}</p>
                    <p className={`text-lg font-bold ${provider.accent}`}>{fmt(totals.solde_reel)} XOF</p>
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-xs text-slate-400 border-b border-white/10">
                            <th className="py-2 pr-3">Application</th>
                            <th className="py-2 pr-3">{t('Crédits', 'Credits')}</th>
                            <th className="py-2 pr-3">{t('Débits', 'Debits')}</th>
                            <th className="py-2 pr-3">{t('Solde théorique', 'Theoretical')}</th>
                            <th className="py-2 pr-3">{t('Commission %', 'Commission %')}</th>
                            <th className="py-2 pr-3">{t('Solde réel', 'Real')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {apps.length === 0 && !hasBaseline && (
                            <tr><td colSpan={6} className="py-5 text-center text-slate-500">{t('Aucun paiement', 'No payments')}</td></tr>
                        )}
                        {apps.map((app) => (
                            <tr key={app.app_name} className="border-b border-white/5" data-testid={`balance-row-${provider.id}-${app.app_name}`}>
                                <td className="py-2.5 pr-3 font-medium text-white">{app.app_name}</td>
                                <td className="py-2.5 pr-3 text-green-400">{fmt(app.credits)} <span className="text-xs text-slate-500">({app.credit_count})</span></td>
                                <td className="py-2.5 pr-3 text-red-400">{fmt(app.debits)} <span className="text-xs text-slate-500">({app.debit_count})</span></td>
                                <td className="py-2.5 pr-3 text-white font-semibold">{fmt(app.solde_theorique)} XOF</td>
                                <td className="py-2.5 pr-3">
                                    <div className="flex items-center gap-1">
                                        <div className="relative">
                                            <Input type="number" min="0" max="100" step="0.5"
                                                value={edits[app.app_name] !== undefined ? edits[app.app_name] : app.commission_pct}
                                                onChange={(e) => setEdits({ ...edits, [app.app_name]: e.target.value })}
                                                className="w-24 h-8 bg-slate-800 border-slate-600 text-white pr-7 text-sm"
                                                data-testid={`commission-input-${provider.id}-${app.app_name}`} />
                                            <Percent size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500" />
                                        </div>
                                        {edits[app.app_name] !== undefined && String(edits[app.app_name]) !== String(app.commission_pct) && (
                                            <Button size="sm" onClick={() => savePct(app.app_name)} disabled={saving === app.app_name}
                                                className="h-8 px-2 bg-blue-600 hover:bg-blue-700" data-testid={`save-commission-${provider.id}-${app.app_name}`}>
                                                <Save size={13} />
                                            </Button>
                                        )}
                                    </div>
                                </td>
                                <td className={`py-2.5 pr-3 font-bold ${provider.accent}`}>{fmt(app.solde_reel)} XOF</td>
                            </tr>
                        ))}
                        {hasManualAdj && (
                            <tr className="border-b border-white/5 bg-slate-800/40" data-testid={`manual-adj-row-${provider.id}`}>
                                <td className="py-2.5 pr-3 text-slate-300 italic">{t('Ajustements manuels', 'Manual adjustments')}</td>
                                <td className="py-2.5 pr-3 text-slate-500">—</td>
                                <td className="py-2.5 pr-3 text-slate-500">—</td>
                                <td className="py-2.5 pr-3 text-white font-semibold">{fmt(manualAdj.delta_theorique)} XOF</td>
                                <td className="py-2.5 pr-3 text-slate-500">—</td>
                                <td className={`py-2.5 pr-3 font-bold ${provider.accent}`}>{fmt(manualAdj.delta_reel)} XOF</td>
                            </tr>
                        )}
                        {hasBaseline && (
                            <tr className="border-b border-white/5 bg-slate-800/40" data-testid={`baseline-row-${provider.id}`}>
                                <td className="py-2.5 pr-3 text-slate-300 italic">{t('Ajustement initial', 'Initial adjustment')}</td>
                                <td className="py-2.5 pr-3 text-slate-500">—</td>
                                <td className="py-2.5 pr-3 text-slate-500">—</td>
                                <td className="py-2.5 pr-3 text-white font-semibold">{fmt(baseline.initial_theorique)} XOF</td>
                                <td className="py-2.5 pr-3 text-slate-500">—</td>
                                <td className={`py-2.5 pr-3 font-bold ${provider.accent}`}>{fmt(baseline.initial_reel)} XOF</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            <ReconCard recon={recon} provider={provider} />
            <ManualTxList transactions={manualTxs} provider={provider} auth={auth} onRefresh={onRefresh} />
            {baseline.ignore_history_before && (
                <p className="text-xs text-amber-400/80 mt-2">
                    ⚠ {t('Transactions antérieures au', 'Transactions before')} {new Date(baseline.ignore_history_before).toLocaleString('fr-FR')} {t('ignorées', 'are ignored')}
                </p>
            )}
            <BaselineDialog open={baselineOpen} onClose={() => setBaselineOpen(false)} provider={provider}
                baseline={baseline} auth={auth} onSaved={onRefresh} />
            <ManualTxDialog open={manualOpen} onClose={() => setManualOpen(false)} provider={provider}
                apps={apps.map((a) => a.app_name)} auth={auth} onSaved={onRefresh} />
        </div>
    );
};

const AppBalances = ({ auth }) => {
    const { t } = useLanguage();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [recon, setRecon] = useState(null);
    const [reconLoading, setReconLoading] = useState(false);

    const runReconciliation = async () => {
        setReconLoading(true);
        try {
            const res = await axios.get(`${API}/payments/apps/reconciliation`, auth);
            setRecon(res.data);
            const both = ['wave', 'orange_money'].map((p) => res.data[p]?.status);
            if (both.every((s) => s === 'ok')) toast.success(t('Soldes conformes, aucun écart ✓', 'Balances match, no gap ✓'));
            else if (both.some((s) => s === 'ecart')) toast.warning(t('Écart détecté — voir le détail par fournisseur', 'Gap detected — see provider details'));
        } catch (e) { toast.error(t('Erreur de rapprochement', 'Reconciliation failed')); }
        finally { setReconLoading(false); }
    };

    const fetchBalances = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/payments/apps/balances`, auth);
            setData(res.data);
        } catch (e) { console.error(e); toast.error(t('Erreur de chargement des soldes', 'Failed to load balances')); }
        finally { setLoading(false); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { fetchBalances(); }, [fetchBalances]);

    if (loading) return <div className="flex justify-center py-12"><div className="spinner"></div></div>;

    const grand = data?.grand_totals || {};

    return (
        <div data-testid="app-balances">
            <div className="flex items-center justify-between mb-5">
                <div className="grid grid-cols-2 gap-4 flex-1 max-w-xl">
                    <div className="glass-card p-4" data-testid="grand-theorique">
                        <p className="text-xs text-slate-400">{t('Solde théorique global (Wave + OM)', 'Global theoretical balance (Wave + OM)')}</p>
                        <p className="text-xl font-bold text-white mt-1">{fmt(grand.solde_theorique)} XOF</p>
                    </div>
                    <div className="glass-card p-4" data-testid="grand-reel">
                        <p className="text-xs text-slate-400 flex items-center gap-1"><Wallet size={13} className="text-blue-400" /> {t('Solde réel global (Wave + OM)', 'Global real balance (Wave + OM)')}</p>
                        <p className="text-xl font-bold text-blue-400 mt-1">{fmt(grand.solde_reel)} XOF</p>
                    </div>
                </div>
                <div className="flex gap-2 ml-4">
                    <Button size="sm" onClick={runReconciliation} disabled={reconLoading}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="reconcile-btn">
                        <Scale size={13} className="mr-1" /> {reconLoading ? t('Rapprochement...', 'Reconciling...') : t('Rapprocher', 'Reconcile')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setLoading(true); fetchBalances(); }}
                        className="border-slate-600 text-slate-300" data-testid="refresh-balances-btn">
                        <RefreshCw size={13} className="mr-1" /> {t('Actualiser', 'Refresh')}
                    </Button>
                </div>
            </div>

            {PROVIDERS.map((p) => (
                <ProviderBalance key={p.id} provider={p} data={data?.providers?.[p.id]} auth={auth} onRefresh={fetchBalances} recon={recon?.[p.id]} />
            ))}

            <p className="text-xs text-slate-500 mt-2">
                {t('Solde théorique = crédits − débits + ajustement initial • Solde réel = commission % × crédits + ajustement initial (transactions réussies uniquement)',
                   'Theoretical = credits − debits + initial adjustment • Real = commission % × credits + initial adjustment (successful transactions only)')}
            </p>
        </div>
    );
};

export default AppBalances;
